import { create } from "zustand";
import { registerOrgScopedReset } from '@/lib/org-scope';
import {
  createInitialDevAgentStoreData,
  type DevAgentStoreData,
} from "./devAgentStoreState";
import type {
  AgentRunStatus,
  ApprovalRequest,
  BackendArtifact,
  DevAgentActiveChangeset,
  DevAgentDependencySnapshot,
  DevAgentPendingFile,
  DevAgentVirtualFile,
  FeedItem,
  TestRunEntry,
  TransparencyReport,
} from "./devAgentStore.types";

export type {
  AgentRunStatus,
  ApprovalRequest,
  BackendArtifact,
  FeedItem,
  SessionCostData,
  TestRunEntry,
  TransparencyReport,
} from "./devAgentStore.types";
export { createDevAgentSessionViewState } from "./devAgentStoreState";

// ── Store ──────────────────────────────────────────────────────────────

interface State extends DevAgentStoreData {
  /** Accepted code for diff view — per-file, keyed by file name. Set by Compare button. */
  /** v4: running conversation summary — bullet points accumulated from each turn */
  /** Dep analysis snapshots — accumulates as the user runs refactor queries.
   *  Each entry is a snapshot of one class's callers + tests at a point in time.
   *  Callers/tests are objects with backend-resolved metadata id (only items
   *  that actually exist in the org are included — never raw class names). */
  setDependencies: (deps: DevAgentDependencySnapshot[]) => void;
  /** Append or upsert a single snapshot — used by SSE push during a run */
  addDependency: (dep: DevAgentDependencySnapshot) => void;
  clearDependencies: () => void;
  /** R/C/M badge per artifact: R=read from org, C=created/generated, M=modified by user */
  /** Server-emitted artifacts keyed by artifact id (`art_...`). Populated by
   *  the `artifact_complete` SSE handler and `listArtifacts()` on session reload.
   *  Consumed by dev-agent `chat/DevAgentChatMessageContent` to rewrite `[ARTIFACT: art_...]` markers
   *  into rendered code/data blocks. */
  setArtifact: (art: BackendArtifact) => void;
  setArtifacts: (arts: BackendArtifact[]) => void;
  /** Line-level coverage data for Monaco decorations */
  setCoverageLines: (className: string, covered: number[], uncovered: number[], pct: number | null) => void;
  clearCoverageLines: () => void;
  /** User-initiated test runs (editor button or panel button). Agent-driven
   *  runs still flow through feedItems. Panel merges both. */
  runTest: (rawClassName: string) => Promise<void>;
  stopTest: () => void;
  // Actions
  setRunStatus: (s: AgentRunStatus) => void;
  addPipelineStep: (label: string) => void;
  clearPipelineSteps: () => void;
  setSessionUuid: (u: string | null) => void;
  setSseTicket: (t: string | null) => void;
  addFeedItem: (item: Omit<FeedItem, "id" | "timestamp">) => void;
  setFeedItems: (items: FeedItem[]) => void;
  updateFeedItem: (id: string, data: Partial<FeedItem["data"]>) => void;
  removeFeedItem: (id: string) => void;
  appendStreamingDelta: (delta: string) => void;
  /** Finalize streaming and append a chat_agent feed item.
   *  `extras` lets the SSE handler bind correlation data (toolCalls, messageUuid)
   *  to THIS specific feed item at creation time, instead of patching "the
   *  latest chat_agent" later (which races with multiple back-to-back messages). */
  finalizeStreaming: (content: string, extras?: Record<string, unknown>) => void;
  closeDrawer: () => void;
  setPendingApproval: (r: ApprovalRequest | null) => void;
  setSuggestedActions: (actions: Array<{ label: string; query: string }>) => void;
  setConnectionStatus: (s: "connected" | "reconnecting" | "disconnected") => void;
  setSfConnected: (v: boolean) => void;
  accumulateTransparencyReport: (report: TransparencyReport) => void;
  setGovernorReport: (report: any) => void;
  setPendingFile: (file: DevAgentPendingFile | null) => void;
  setEditorOpen: (open: boolean) => void;
  setAcceptedCode: (code: string | null, target?: string) => void;
  setActiveChangeset: (changeset: DevAgentActiveChangeset | null) => void;
  addVirtualFile: (id: string, file: DevAgentVirtualFile) => void;
  setConversationSummary: (bullets: string[]) => void;
  addToArtifacts: (file: DevAgentPendingFile) => void;
  removeFromArtifacts: (fileId: string) => void;
  isInArtifacts: (fileId: string) => boolean;
  setArtifactBadge: (id: string, badge: "R" | "C" | "M") => void;
  setDismissedPanels: (panels: Set<string>) => void;
  dismissPanel: (panel: string) => void;
  reset: () => void;
}

const init = createInitialDevAgentStoreData();

export const useDevAgentStore = create<State>()((set, get) => ({
  ...init,
  setRunStatus: (runStatus) => set({ runStatus }),
  setSessionUuid: (sessionUuid) =>
    set((s) =>
      s.sessionUuid === sessionUuid ? { sessionUuid } : { sessionUuid, sfConnected: true },
    ),
  setSseTicket: (sseTicket: string | null) => set({ sseTicket }),

  addFeedItem: (item) => set((s) => {
    const id = crypto.randomUUID();
    const newItem = { ...item, id, timestamp: Date.now() };
    // Auto-clear dismissed panel when new data arrives for it
    const dp = s.dismissedPanels;
    let nextDp = dp;
    if (dp.size > 0) {
      const t = item.type;
      if ((t === "impact_analysis" && dp.has("impact")) ||
          (t === "test_coverage" && dp.has("tests")) ||
          (t === "tool_call" && dp.has("agents")) ||
          (t === "code_change" && dp.has("approvals"))) {
        nextDp = new Set(dp);
        if (t === "impact_analysis") nextDp.delete("impact");
        if (t === "test_coverage") nextDp.delete("tests");
        if (t === "tool_call") nextDp.delete("agents");
        if (t === "code_change") nextDp.delete("approvals");
      }
    }
    return { feedItems: [...s.feedItems, newItem], dismissedPanels: nextDp };
  }),

  setFeedItems: (items) => set({ feedItems: items }),

  updateFeedItem: (id, data) => set((s) => ({
    feedItems: s.feedItems.map((f) => f.id === id ? { ...f, data: { ...f.data, ...data } } : f),
  })),

  removeFeedItem: (id) => set((s) => ({
    feedItems: s.feedItems.filter((f) => f.id !== id),
  })),

  appendStreamingDelta: (delta) => set((s) => ({
    streamingContent: (s.streamingContent ?? "") + delta,
  })),

  finalizeStreaming: (content, extras) => set((s) => {
    if (s.streamingContent === null) return s;
    return {
      streamingContent: null,
      feedItems: [
        ...s.feedItems,
        {
          id: crypto.randomUUID(),
          type: "chat_agent",
          timestamp: Date.now(),
          data: { content: content || s.streamingContent || "", ...(extras || {}) },
        },
      ],
    };
  }),

  closeDrawer: () => set({ drawerOpen: false, drawerItem: null }),
  setPendingApproval: (pendingApproval) => set({ pendingApproval }),
  addPipelineStep: (label) => set((s) => {
    const last = s.pipelineSteps[s.pipelineSteps.length - 1];
    if (last && last.label === label && last.status === "running") return s;
    const steps = s.pipelineSteps.map((st) => ({ ...st, status: "done" as const }));
    return { pipelineSteps: [...steps, { label, status: "running" as const }] };
  }),
  clearPipelineSteps: () => set({ pipelineSteps: [] }),
  setPendingFile: (pendingFile) => set({ pendingFile }),
  setEditorOpen: (editorOpen) => set({ editorOpen }),
  setAcceptedCode: (acceptedCode, target?: string) => set({ acceptedCode, acceptedCodeTarget: target || null }),
  setActiveChangeset: (changeset) => set({ activeChangeset: changeset }),
  addVirtualFile: (id, file) => set((s) => ({ virtualFiles: { ...s.virtualFiles, [id]: file } })),
  setConversationSummary: (conversationSummary) => set({ conversationSummary }),
  setDependencies: (dependencies) => set({ dependencies: Array.isArray(dependencies) ? dependencies : (dependencies ? [dependencies as any] : []) }),
  addDependency: (dep) => set((s) => {
    const filtered = (s.dependencies || []).filter((d) => d.target !== dep.target);
    return { dependencies: [...filtered, dep].slice(-30) };
  }),
  clearDependencies: () => set({ dependencies: [] }),
  setCoverageLines: (className, covered_lines, uncovered_lines, coverage_pct) =>
    set({ coverageLines: { className, covered_lines, uncovered_lines, coverage_pct } }),
  clearCoverageLines: () => set({ coverageLines: null }),
  stopTest: () => set({ runningTest: null }),
  runTest: async (testClassName: string) => {
    const uuid = get().sessionUuid;
    if (!uuid) return;
    if (get().runningTest) return;
    set({ runningTest: { testClassName, startedAt: Date.now() } });
    const isStillActive = () => get().runningTest?.testClassName === testClassName;
    const { default: toast } = await import("react-hot-toast");
    try {
      const { runApexTest } = await import("@/pages/dev-agent/devAgentService");
      const r = await runApexTest(uuid, { test_class: testClassName });
      if (!isStillActive()) return;
      const entry: TestRunEntry = {
        testClassName,
        className: r.production_class ?? null,
        coveredClasses: r.covered_classes ?? [],
        success: !!r.success,
        summary: r.result || (r.success ? "Tests passed" : "Tests failed"),
        rawResult: r.result,
        passed: r.passed,
        failed: r.failed,
        total: r.total,
        ts: Date.now(),
      };
      set((s) => ({
        testRuns: [...s.testRuns.filter((t) => t.testClassName !== testClassName), entry].slice(-20),
      }));
      if (entry.success) {
        toast.success(`${testClassName}: ${entry.passed ?? 0}/${entry.total ?? 0} passed`);
      } else {
        toast.error(`${testClassName}: ${entry.failed ?? 0}/${entry.total ?? 0} failed`);
      }
    } catch {
      if (!isStillActive()) return;
      set((s) => ({
        testRuns: [...s.testRuns.filter((t) => t.testClassName !== testClassName), {
          testClassName,
          className: null,
          coveredClasses: [],
          success: false,
          summary: "Test execution failed",
          ts: Date.now(),
        }].slice(-20),
      }));
      toast.error(`${testClassName}: failed to run`);
    } finally {
      if (get().runningTest?.testClassName === testClassName) {
        set({ runningTest: null });
      }
    }
  },
  addToArtifacts: (file) => {
    const state = get();
    if (state.pinnedArtifactIds[file.id]) return;
    const existing = state.feedItems.find(
      (f) => f.type === "tool_call" && f.data.status === "done" &&
             (f.data.args?.name === file.name || f.data.metadataId === file.id)
    );
    if (!existing) {
      state.addFeedItem({
        type: "tool_call",
        data: {
          name: "read_source_code",
          args: { name: file.name },
          status: "done",
          output: file.content || "loaded",
          metadataType: file.metadataType || "ApexClass",
          metadataId: file.id,
          content: file.content || "",
        },
      });
    }
    set({ pinnedArtifactIds: { ...state.pinnedArtifactIds, [file.id]: true } });
    const uuid = state.sessionUuid;
    if (uuid) {
      const u = uuid;
      import("@/pages/dev-agent/devAgentService").then(({ addArtifact }) => {
        addArtifact(u, { name: file.name, metadataType: file.metadataType || "", metadataId: String(file.id || "") }).catch(() => {});
      });
    }
  },
  removeFromArtifacts: (fileId) => {
    const state = get();
    const item = state.feedItems.find(
      (f) => f.type === "tool_call" && f.data.status === "done" && f.data.metadataId === fileId
    );
    if (item) state.removeFeedItem(item.id);
    const { [fileId]: _, ...rest } = state.pinnedArtifactIds;
    set({ pinnedArtifactIds: rest });
    const uuid = state.sessionUuid;
    if (uuid) {
      import("@/pages/dev-agent/devAgentService").then(({ removeArtifact }) => {
        removeArtifact(uuid, fileId).catch(() => {});
      });
    }
  },
  isInArtifacts: (fileId) => !!get().pinnedArtifactIds[fileId],
  setArtifactBadge: (id, badge) => set((s) => ({ artifactBadges: { ...s.artifactBadges, [id]: badge } })),
  setArtifact: (art) => set((s) => ({ artifactsById: { ...s.artifactsById, [art.id]: art } })),
  setArtifacts: (arts) => set((s) => {
    const next: Record<string, BackendArtifact> = { ...s.artifactsById };
    for (const a of arts) {
      const existing = next[a.id];
      if (existing?.content && !a.content) {
        next[a.id] = { ...a, ...existing };
        continue;
      }
      next[a.id] = { ...existing, ...a };
    }
    return { artifactsById: next };
  }),
  setDismissedPanels: (panels) => set({ dismissedPanels: panels }),
  dismissPanel: (panel) => set((s) => { const next = new Set(s.dismissedPanels); next.add(panel); return { dismissedPanels: next }; }),
  setSuggestedActions: (suggestedActions) => set({ suggestedActions }),
  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),
  setSfConnected: (sfConnected) => set({ sfConnected }),
  accumulateTransparencyReport: (report) => set((s) => {
    const reqCost = report.total_cost_usd || 0;
    const prev = s.sessionCost;
    const newCount = prev.requestCount + 1;
    const newCacheRate = report.cache_hit_rate != null
      ? (prev.cacheHitRate * prev.requestCount + report.cache_hit_rate) / newCount
      : prev.cacheHitRate;
    return {
      transparencyReport: report,
      sessionCost: {
        totalCostUsd: prev.totalCostUsd + reqCost,
        totalInputTokens: prev.totalInputTokens + (report.input_tokens || 0),
        totalOutputTokens: prev.totalOutputTokens + (report.output_tokens || 0),
        requestCount: newCount,
        lastRequestCostUsd: reqCost,
        cacheHitRate: newCacheRate,
      },
    };
  }),
  setGovernorReport: (governorReport) => set({ governorReport }),
  reset: () => set(init),
}));

// ── Organization scoping ─────────────────────────────────────────────────────
//
// The dev agent's state is a working session against one organization's code:
// the open file, the generated patch, the conversation about it.
registerOrgScopedReset('devAgentStore', () => {
  useDevAgentStore.getState().reset();
});
